import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { AdminSection } from "./admin";
import { AdminButton, AdminDialog, Field, Input, Row, TableShell, Textarea } from "@/components/admin/AdminUI";
import { MediaUpload } from "@/components/admin/MediaUpload";

export const Route = createFileRoute("/_authenticated/admin/books")({
  component: BooksAdmin,
});

type B = Database["public"]["Tables"]["books"]["Row"];
type Ins = Database["public"]["Tables"]["books"]["Insert"];
type DT = { slug: string; title: string };

const EMPTY: Ins = {
  slug: "", title: "", subtitle: "", long_description: "",
  cover_url: "", purchase_url: "", sort_order: 0,
};

function BooksAdmin() {
  const [rows, setRows] = useState<B[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<B | "new" | null>(null);
  const [digitalTitles, setDigitalTitles] = useState<DT[]>([]);

  async function load() {
    setLoading(true);
    const [{ data: books }, { data: titles }] = await Promise.all([
      supabase.from("books").select("*").order("sort_order").order("created_at"),
      supabase.from("digital_titles").select("slug,title").eq("kind", "book").order("title"),
    ]);
    setRows(books ?? []);
    setDigitalTitles((titles as DT[] | null) ?? []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function remove(id: string) {
    if (!confirm("Delete this book?")) return;
    const { error } = await supabase.from("books").delete().eq("id", id);
    if (error) alert(error.message); else load();
  }

  return (
    <AdminSection
      title="Books"
      description="External book listings shown on /books — cover, description, and outbound purchase link (Apple Books, Amazon, etc.). This is NOT the same as a full in-app readable edition: those live in Library → Digital Titles (kind = book), which drive /library/read/$slug with highlights, bookmarks, and reading progress. If a book has both a listing here AND a full readable edition, use the 'Full in-app edition' field below to link them — the public book page will then show a 'Read the full edition' link."
      action={<AdminButton onClick={() => setEditing("new")}>+ New book</AdminButton>}
    >
      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> :
       rows.length === 0 ? <p className="text-sm text-muted-foreground">None yet.</p> :
        <TableShell
          headers={["Order", "Title", "Subtitle", ""]}
          rows={rows.map((r) => (
            <tr key={r.id} className="hover:bg-obsidian/40">
              <td className="px-4 py-3 text-muted-foreground">{r.sort_order}</td>
              <td className="px-4 py-3">
                <div className="font-serif text-base text-gold-luminous">{r.title}</div>
                <div className="text-xs text-muted-foreground">/{r.slug}</div>
              </td>
              <td className="px-4 py-3 text-sm text-muted-foreground">{r.subtitle ?? "—"}</td>
              <td className="px-4 py-3">
                <div className="flex justify-end gap-2">
                  <AdminButton variant="ghost" onClick={() => setEditing(r)}>Edit</AdminButton>
                  <AdminButton variant="danger" onClick={() => remove(r.id)}>Delete</AdminButton>
                </div>
              </td>
            </tr>
          ))}
        />
      }
      {editing && (
        <Form
          initial={editing === "new" ? EMPTY : editing}
          digitalTitles={digitalTitles}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </AdminSection>
  );
}

function Form({ initial, digitalTitles, onClose, onSaved }: { initial: B | Ins; digitalTitles: DT[]; onClose: () => void; onSaved: () => void }) {
  const isNew = !("id" in initial);
  // Cast so newly added columns are addressable regardless of the current generated Row type.
  const src = initial as unknown as Record<string, unknown>;
  const [f, setF] = useState({
    slug: (src.slug as string | null) ?? "",
    title: (src.title as string | null) ?? "",
    subtitle: (src.subtitle as string | null) ?? "",
    long_description: (src.long_description as string | null) ?? "",
    cover_url: (src.cover_url as string | null) ?? "",
    cover_alt: (src.cover_alt as string | null) ?? "",
    purchase_url: (src.purchase_url as string | null) ?? "",
    purchase_label: (src.purchase_label as string | null) ?? "",
    author: (src.author as string | null) ?? "",
    author_legal_name: (src.author_legal_name as string | null) ?? "",
    category: (src.category as string | null) ?? "",
    tags: Array.isArray(src.tags) ? (src.tags as string[]).join(", ") : "",
    featured: Boolean(src.featured ?? false),
    digital_title_slug: (src.digital_title_slug as string | null) ?? "",
    sort_order: (src.sort_order as number | null)?.toString() ?? "0",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault(); setBusy(true); setError(null);
    const payload = {
      slug: f.slug.trim(),
      title: f.title.trim(),
      subtitle: f.subtitle.trim() || null,
      long_description: f.long_description,
      cover_url: f.cover_url.trim() || null,
      cover_alt: f.cover_alt.trim() || null,
      purchase_url: f.purchase_url.trim() || null,
      purchase_label: f.purchase_label.trim() || null,
      author: f.author.trim() || null,
      author_legal_name: f.author_legal_name.trim() || null,
      category: f.category.trim() || null,
      tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
      featured: f.featured,
      digital_title_slug: f.digital_title_slug || null,
      sort_order: parseInt(f.sort_order, 10) || 0,
    } as unknown as Ins;
    const q = isNew ? supabase.from("books").insert(payload) : supabase.from("books").update(payload).eq("id", (initial as B).id);
    const { error } = await q;
    setBusy(false);
    if (error) setError(error.message); else onSaved();
  }

  return (
    <AdminDialog open onClose={onClose} title={isNew ? "New book" : `Edit · ${initial.title}`}>
      <form onSubmit={submit} className="space-y-4">
        <Row>
          <Field label="Title"><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
          <Field label="Slug"><Input required value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} /></Field>
        </Row>
        <Field label="Subtitle"><Input value={f.subtitle} onChange={(e) => setF({ ...f, subtitle: e.target.value })} /></Field>
        <Field label="Long description"><Textarea rows={6} value={f.long_description ?? ""} onChange={(e) => setF({ ...f, long_description: e.target.value })} /></Field>
        <Row>
          <Field label="Author (display name)"><Input value={f.author} onChange={(e) => setF({ ...f, author: e.target.value })} /></Field>
          <Field label="Author legal name (optional)"><Input value={f.author_legal_name} onChange={(e) => setF({ ...f, author_legal_name: e.target.value })} /></Field>
        </Row>
        <Row>
          <Field label="Category"><Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} /></Field>
          <Field label="Tags (comma-separated)"><Input value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} /></Field>
        </Row>
        <Row>
          <Field label="Cover image">
            <MediaUpload value={f.cover_url} onChange={(url) => setF({ ...f, cover_url: url })} folder="books" />
          </Field>
          <Field label="Cover alt text"><Input value={f.cover_alt} onChange={(e) => setF({ ...f, cover_alt: e.target.value })} /></Field>
        </Row>
        <Row>
          <Field label="External purchase URL (Apple Books, Amazon, etc.)"><Input value={f.purchase_url} onChange={(e) => setF({ ...f, purchase_url: e.target.value })} /></Field>
          <Field label="Purchase CTA label"><Input placeholder="Read on Apple Books" value={f.purchase_label} onChange={(e) => setF({ ...f, purchase_label: e.target.value })} /></Field>
        </Row>
        <Field label="Full in-app edition (Digital Title of kind 'book')">
          <select
            className="w-full rounded-md border hairline bg-obsidian px-3 py-2 text-sm"
            value={f.digital_title_slug}
            onChange={(e) => setF({ ...f, digital_title_slug: e.target.value })}
          >
            <option value="">— none —</option>
            {digitalTitles.map((d) => (
              <option key={d.slug} value={d.slug}>{d.title} ({d.slug})</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-muted-foreground">Optional. When set, the public book page shows a "Read the full edition" link to /library/read/{`{slug}`}.</p>
        </Field>
        <Row>
          <Field label="Sort order"><Input type="number" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: e.target.value })} /></Field>
          <Field label="Featured">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} />
              Promote in Featured Book modules
            </label>
          </Field>
        </Row>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <div className="flex justify-end gap-2 border-t hairline pt-4">
          <AdminButton type="button" variant="ghost" onClick={onClose}>Cancel</AdminButton>
          <AdminButton type="submit" disabled={busy}>{busy ? "Saving…" : isNew ? "Create" : "Save"}</AdminButton>
        </div>
      </form>
    </AdminDialog>
  );
}
